<template>
	<portal :to="portal">
		<Trap :disabled="!showTrap">
			<helio-pop
				:active="active"
				:anchor="anchor"
				:close-on-outside-click="true"
				:title="title"
				:arrow="true"
				:state="state"
				:initial-position="position"
				:parent-class="popClassName"
				:parent-position="parentPosition"
				:initial-offset="offset"
				:gap="gap"
				:width="width"
				class="popconfirm"
				skin="light"
				shadow="small"
				role="dialog"
				aria-relevant="additions text"
				aria-live="polite"
				aria-atomic="false"
				@keyup.esc="$emit('close')"
				@close="$emit('close')"
				@open="$emit('open')"
			>
				<template slot="content">
					<slot name="content" />
				</template>

				<template slot="footer">
					<helio-button-group
						:segmented="false"
						alignment="right"
					>
						<helio-button
							:text="cancel"
							color="normal"
							data-cy="close"
							size="small"
							@click="$emit('cancel')"
						/>
						<helio-button
							:color="state"
							:text="confirm"
							size="small"
							@click="$emit('confirm')"
						/>
					</helio-button-group>
				</template>
			</helio-pop>
		</Trap>
	</portal>
</template>

<script>
import Trap from 'vue-focus-lock';
import Pop from '../pop/Pop.vue';
import ButtonGroup from '../button-group/ButtonGroup.vue';
import Button from '../button/Button.vue';

export default {
	name: 'Popconfirm',

	components: {
		Trap,
		'helio-pop': Pop,
		'helio-button-group': ButtonGroup,
		'helio-button': Button,
	},

	props: {
		active: {
			type: Boolean,
			required: true,
			description: 'Sets whether the Popconfirm will be displayed',
		},

		anchor: {
			type: String,
			required: true,
			description: 'Sets which element will control the Popconfirm\'s position',
		},

		cancel: {
			type: String,
			required: true,
			description: 'Sets the text on the cancel button',
		},

		confirm: {
			type: String,
			required: true,
			description: 'Sets the text on the confirm button',
		},

		gap: {
			type: Number,
			required: false,
			default: 12,
			description: 'Sets the distance between the Popconfirm and its anchor element',
		},

		offset: {
			type: String,
			required: false,
			default: 'center',
			validator: value => [
				'bottom',
				'center',
				'left',
				'right',
				'top',
			].indexOf(value) !== -1,
			description: 'Sets the offset of the Popconfirm in relation to its anchor element',
		},

		parentPosition: {
			type: String,
			required: false,
			default: 'normal',
			validator: value => [
				'fixed',
				'normal',
			].indexOf(value) !== -1,
		},

		portal: {
			type: String,
			required: false,
			default: 'pop',
			description: 'Sets the target portal to use',
		},

		position: {
			type: String,
			required: false,
			default: 'top',
			validator: value => [
				'left',
				'bottom',
				'top',
				'right',
			].indexOf(value) !== -1,
			description: 'Sets where the Popconfirm will appear in relation to its anchor element',
		},

		state: {
			type: String,
			required: false,
			default: 'info',
			validator: value => [
				'alert',
				'danger',
				'info',
				'success',
			].indexOf(value) !== -1,
			description: 'Sets the state of the Popconfirm',
		},

		title: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Popconfirm\'s title',
		},

		width: {
			type: Number,
			required: false,
			default: null,
			description: 'Sets a custom width for the Popconfirm',
		},
	},

	slots: {
		content: {
			required: false,
			scoped: false,
			description: 'Supplies the content of the Popconfirm component',
		},
	},

	data() {
		return {
			showTrap: false,
			lastFocusedElement: null,
			popClassName: '',
		};
	},

	watch: {
		active() {
			if (this.active) {
				this.showTrap = this.active;
				this.lastFocusedElement = document.activeElement;
			}

			if (!this.active) {
				this.showTrap = this.active;
				setTimeout(() => {
					this.lastFocusedElement.focus();
				}, 100);
			}
		},
	},

	mounted() {
		window.addEventListener('keyup', this.checkForEscape);

		if (this.$el && this.$el.classList && this.$el.classList.value) {
			this.popClassName = this.$el.classList.value.replace('v-portal', '');
		}
	},

	beforeDestroy() {
		window.removeEventListener('keyup', this.checkForEscape);
	},

	methods: {
		checkForEscape(event) {
			const { keyCode } = event;

			if (keyCode === 27) {
				this.$emit('close');
			}
		},

		close(emit = true) {
			this.showTrap = false;

			setTimeout(() => {
				this.lastFocusedElement.focus();
			}, 100);

			if (emit) {
				this.$emit('close');
			}
		},
	},
};
</script>

<style lang="scss" src="./popconfirm.scss" />
